import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Initialize Twilio SMS service

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppointmentsProvider } from './contexts/AppointmentsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CalendarSettingsProvider } from './contexts/CalendarSettingsContext';
import { PinLockProvider } from './contexts/PinLockContext';
import { LocationProvider } from './contexts/LocationContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import PermGate from './components/PermGate';

// Layouts
import MainLayout from './layouts/MainLayout';

// Critical pages — loaded immediately (dashboard, login, core POS)
import LoginPage from './pages/LoginPage';
import ActivatePage from './pages/ActivatePage';
import Dashboard from './components/Dashboard/Dashboard';
import POSPage from './pages/POSPage';
import CheckInPage from './pages/CheckInPage';
import { CompleteBookingSystem } from './components/CompleteBookingSystem';

// Emergency Notification Popup (Global — must be eager)
import { EmergencyBookingPopupNotification } from './components/staff/EmergencyBookingPopupNotification';
// Operator Alert Popup (Global — must be eager): barber-routed arrival/emergency/message/waitlist alerts.
// TWO looks exist — swap the mount below to compare. V1 (boxed) is kept; V2 (Pro) mimics the Emergency popup.
// import { OperatorAlertPopup } from './components/staff/OperatorAlertPopup'; // v1 — boxed (kept for comparison)
import { OperatorAlertPopupPro } from './components/staff/OperatorAlertPopupPro'; // v2 — freestanding head + rings
// Shared celebration "success moment" surface (confetti + card) — fired on key completions.
import { CelebrationProvider } from './components/ui/CelebrationProvider';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import SlikLoader from './components/branding/SlikLoader';

// Loading fallback for lazy routes — branded inline spinner.
const PageLoader = () => <SlikLoader label="Loading..." />;

// Lazy-loaded pages — split into separate chunks
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const LoyaltyPage = React.lazy(() => import('./pages/LoyaltyPage'));
const GamificationPage = React.lazy(() => import('./pages/GamificationPage'));
const FinancialPage = React.lazy(() => import('./pages/FinancialPage'));
const CommunicationPage = React.lazy(() => import('./pages/CommunicationPage'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
const MarketingPage = React.lazy(() => import('./pages/MarketingPage'));
const DigitalCoinsPage = React.lazy(() => import('./pages/DigitalCoinsPage'));
const PartnershipPage = React.lazy(() => import('./pages/PartnershipPage'));
const ReviewsPage = React.lazy(() => import('./pages/ReviewsPage'));
const ContentOwnershipPage = React.lazy(() => import('./pages/ContentOwnershipPage'));
const AIManagerPage = React.lazy(() => import('./pages/AIManagerPage'));
const TrainingPage = React.lazy(() => import('./pages/TrainingPage'));
const DataImportPage = React.lazy(() => import('./pages/DataImportPage'));
const PeoplePage = React.lazy(() => import('./pages/PeoplePage'));
// ShopSettingsPage removed — merged into unified SettingsPage
const ServiceManagementPage = React.lazy(() => import('./pages/ServiceManagementPage'));
const BarberReportsPage = React.lazy(() => import('./pages/BarberReportsPage'));
const ServiceSalesReportPage = React.lazy(() => import('./pages/ServiceSalesReportPage'));
const ProductSalesReportPage = React.lazy(() => import('./pages/ProductSalesReportPage'));
const BarberPerformanceReportPage = React.lazy(() => import('./pages/BarberPerformanceReportPage'));
const MeetingStudioPage = React.lazy(() => import('./pages/MeetingStudioPage'));
const CoachingDashboardPage = React.lazy(() => import('./pages/CoachingDashboardPage'));
const MyCoachingPage = React.lazy(() => import('./pages/MyCoachingPage'));
const ClientAnalyticsReportPage = React.lazy(() => import('./pages/ClientAnalyticsReportPage'));
const FinanceSummaryReportPage = React.lazy(() => import('./pages/FinanceSummaryReportPage'));
const ProfitLossReportPage = React.lazy(() => import('./pages/ProfitLossReportPage'));
const SalesTaxReportPage = React.lazy(() => import('./pages/SalesTaxReportPage'));
const GrowthReportPage = React.lazy(() => import('./pages/GrowthReportPage'));
const InventoryReportPage = React.lazy(() => import('./pages/InventoryReportPage'));
const BusinessIntelligencePage = React.lazy(() => import('./pages/BusinessIntelligencePage'));
const GiftCardManagementPage = React.lazy(() => import('./pages/GiftCardManagementPage'));
const OperationsPage = React.lazy(() => import('./pages/OperationsPage'));
const AISettingsPage = React.lazy(() => import('./pages/AISettingsPage'));
const FirebaseTestPage = React.lazy(() => import('./pages/FirebaseTestPage'));
const ViewExistingDataPage = React.lazy(() => import('./pages/ViewExistingDataPage'));
const ImportDataPage = React.lazy(() => import('./pages/ImportDataPage'));

// Helper: wrap a lazy page in Suspense + ProtectedRoute + MainLayout.
// Optional gate (Jose 2026-09-24): pass `managerUp`/`perm`/`ownerOnly` to wall a business-side page
// off from a barber operator (shop-account + PIN model). Barbers get calendar + register + profile;
// everything else is manager/owner. An owner logged in as themselves passes every gate.
function LazyProtectedRoute(
  { children, managerUp, perm, ownerOnly, label }:
  { children: React.ReactNode; managerUp?: boolean; perm?: string; ownerOnly?: boolean; label?: string }
) {
  const gated = (managerUp || perm || ownerOnly)
    ? <PermGate managerUp={managerUp} perm={perm} ownerOnly={ownerOnly} label={label}>{children}</PermGate>
    : children;
  return (
    <ProtectedRoute>
      <MainLayout>
        {/* ChunkErrorBoundary: if a stale-cache device 404s a route chunk, auto-reload to the
            fresh build instead of hanging on the spinner forever. Jose 2026-09-18. */}
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            {gated}
          </Suspense>
        </ChunkErrorBoundary>
      </MainLayout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <LocationProvider>
            <PinLockProvider>
              <NotificationProvider>
                <AppointmentsProvider>
                  <CalendarSettingsProvider>
                    <CelebrationProvider>
                    <Toaster position="top-right" containerStyle={{ zIndex: 100000 }} />
                  {/* Global Emergency Booking Popup - shows when emergency bookings are created */}
                  <EmergencyBookingPopupNotification />
                  {/* Global Operator Alerts - barber-routed (arrival/emergency/message/waitlist), shows
                      only on the device pinned in as the target barber. One-brain: operatorAlertService.
                      V2 (Pro) = Emergency-style freestanding head + rings; swap to <OperatorAlertPopup/> for v1. */}
                  <OperatorAlertPopupPro />
                  <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  {/* Station activation link — signs a barber's device into the shop account via a
                      one-time token (no password). Public route; redeem is gated server-side. */}
                  <Route path="/activate" element={<ActivatePage />} />
                  {/* Public self-registration REMOVED (Jose 2026-10-12): a barber who couldn't open her
                      activation link signed up here → a role-less account disconnected from her barber
                      record → bare app. Staff are provisioned + onboard via activation links, never self-signup. */}
                  <Route path="/firebase-test" element={
                    <Suspense fallback={<PageLoader />}><FirebaseTestPage /></Suspense>
                  } />

                  {/* Critical Routes — eager loaded */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute><MainLayout><PermGate managerUp label="Dashboard"><Dashboard /></PermGate></MainLayout></ProtectedRoute>
                  } />
                  {/* /calendar renders the TABBED OperationsPage (defaults to the Calendar tab), NOT a bare
                      CalendarView — otherwise the Calendar/Appointments/Waitlist strip is MISSING here. This
                      route is the app DEFAULT + catch-all + post-login landing, so a transient bounce to it
                      ("when something goes stale") was dropping staff onto a tab-less calendar. (Jose 2026-10-25) */}
                  <Route path="/calendar" element={
                    <LazyProtectedRoute><OperationsPage /></LazyProtectedRoute>
                  } />
                  <Route path="/pos" element={
                    <ProtectedRoute><MainLayout><POSPage /></MainLayout></ProtectedRoute>
                  } />
                  <Route path="/check-in" element={
                    <ProtectedRoute><MainLayout><CheckInPage /></MainLayout></ProtectedRoute>
                  } />
                  <Route path="/booking" element={
                    <ProtectedRoute><MainLayout><CompleteBookingSystem /></MainLayout></ProtectedRoute>
                  } />

                  {/* Lazy-loaded Routes. managerUp = walled off from a barber operator (shop-account +
                      PIN model, Jose 2026-09-24): barbers get calendar + register + their profile;
                      everything below is manager/owner. An owner logged in as themselves passes. */}
                  <Route path="/services" element={<LazyProtectedRoute managerUp label="Services"><ServiceManagementPage /></LazyProtectedRoute>} />
                  <Route path="/analytics" element={<LazyProtectedRoute managerUp label="Analytics"><AnalyticsPage /></LazyProtectedRoute>} />
                  <Route path="/inventory" element={<LazyProtectedRoute managerUp label="Inventory"><InventoryPage /></LazyProtectedRoute>} />
                  <Route path="/loyalty" element={<LazyProtectedRoute managerUp label="Loyalty"><LoyaltyPage /></LazyProtectedRoute>} />
                  <Route path="/gamification" element={<LazyProtectedRoute managerUp label="Gamification"><GamificationPage /></LazyProtectedRoute>} />
                  <Route path="/financial" element={<LazyProtectedRoute managerUp label="Financials"><FinancialPage /></LazyProtectedRoute>} />
                  <Route path="/communication" element={<LazyProtectedRoute managerUp label="Communication"><CommunicationPage /></LazyProtectedRoute>} />
                  <Route path="/security" element={<LazyProtectedRoute managerUp label="Security"><SecurityPage /></LazyProtectedRoute>} />
                  <Route path="/marketing" element={<LazyProtectedRoute managerUp label="Marketing"><MarketingPage /></LazyProtectedRoute>} />
                  <Route path="/digital-coins" element={<LazyProtectedRoute managerUp label="Digital Coins"><DigitalCoinsPage /></LazyProtectedRoute>} />
                  <Route path="/partnership" element={<LazyProtectedRoute managerUp label="Partnership"><PartnershipPage /></LazyProtectedRoute>} />
                  <Route path="/reviews" element={<LazyProtectedRoute managerUp label="Reviews"><ReviewsPage /></LazyProtectedRoute>} />
                  <Route path="/content-ownership" element={<LazyProtectedRoute managerUp label="Content"><ContentOwnershipPage /></LazyProtectedRoute>} />
                  <Route path="/ai-manager" element={<LazyProtectedRoute managerUp label="AI Manager"><AIManagerPage /></LazyProtectedRoute>} />
                  <Route path="/training" element={<LazyProtectedRoute managerUp label="Training"><TrainingPage /></LazyProtectedRoute>} />
                  <Route path="/data-import" element={<LazyProtectedRoute managerUp label="Data Import"><DataImportPage /></LazyProtectedRoute>} />
                  <Route path="/people" element={<LazyProtectedRoute managerUp label="People"><PeoplePage /></LazyProtectedRoute>} />
                  {/* /shop-settings removed — merged into unified /settings */}
                  {/* /service-management consolidated into /services (Jose 2026-10-17) — redirect stale links */}
                  <Route path="/service-management" element={<Navigate to="/services" replace />} />
                  <Route path="/barber-reports" element={<LazyProtectedRoute managerUp label="Reports"><BarberReportsPage /></LazyProtectedRoute>} />
                  <Route path="/service-sales-report" element={<LazyProtectedRoute managerUp label="Reports"><ServiceSalesReportPage /></LazyProtectedRoute>} />
                  <Route path="/product-sales-report" element={<LazyProtectedRoute managerUp label="Reports"><ProductSalesReportPage /></LazyProtectedRoute>} />
                  <Route path="/barber-performance-report" element={<LazyProtectedRoute managerUp label="Reports"><BarberPerformanceReportPage /></LazyProtectedRoute>} />
                  <Route path="/meeting-studio" element={<LazyProtectedRoute managerUp label="Meeting Studio"><MeetingStudioPage /></LazyProtectedRoute>} />
                  <Route path="/coaching" element={<LazyProtectedRoute managerUp label="Coaching"><CoachingDashboardPage /></LazyProtectedRoute>} />
                  {/* barber's OWN coaching — open to barbers */}
                  <Route path="/my-coaching" element={<LazyProtectedRoute><MyCoachingPage /></LazyProtectedRoute>} />
                  <Route path="/client-analytics-report" element={<LazyProtectedRoute managerUp label="Reports"><ClientAnalyticsReportPage /></LazyProtectedRoute>} />
                  <Route path="/finance-summary-report" element={<LazyProtectedRoute managerUp label="Reports"><FinanceSummaryReportPage /></LazyProtectedRoute>} />
                  <Route path="/profit-loss-report" element={<LazyProtectedRoute managerUp label="Reports"><ProfitLossReportPage /></LazyProtectedRoute>} />
                  <Route path="/sales-tax-report" element={<LazyProtectedRoute managerUp label="Reports"><SalesTaxReportPage /></LazyProtectedRoute>} />
                  <Route path="/growth-report" element={<LazyProtectedRoute managerUp label="Reports"><GrowthReportPage /></LazyProtectedRoute>} />
                  <Route path="/inventory-report" element={<LazyProtectedRoute managerUp label="Reports"><InventoryReportPage /></LazyProtectedRoute>} />
                  <Route path="/business-intelligence" element={<LazyProtectedRoute managerUp label="Business Intelligence"><BusinessIntelligencePage /></LazyProtectedRoute>} />
                  <Route path="/gift-cards" element={<LazyProtectedRoute managerUp label="Gift Cards"><GiftCardManagementPage /></LazyProtectedRoute>} />
                  {/* Operations = the calendar + appointments + booking hub — a barber's main view. OPEN. */}
                  <Route path="/operations" element={<LazyProtectedRoute><OperationsPage /></LazyProtectedRoute>} />
                  <Route path="/ai-settings" element={<LazyProtectedRoute managerUp label="AI Settings"><AISettingsPage /></LazyProtectedRoute>} />
                  <Route path="/settings" element={<LazyProtectedRoute managerUp label="Settings"><SettingsPage /></LazyProtectedRoute>} />
                  <Route path="/view-data" element={<LazyProtectedRoute managerUp label="Data"><ViewExistingDataPage /></LazyProtectedRoute>} />
                  <Route path="/import-data" element={<LazyProtectedRoute managerUp label="Data"><ImportDataPage /></LazyProtectedRoute>} />

                  {/* Default redirect → calendar (the universal, ungated home for every role; the
                      dashboard is manager/owner-gated, so we never land anyone on a blocked page). */}
                  <Route path="/" element={<Navigate to="/calendar" replace />} />
                  <Route path="*" element={<Navigate to="/calendar" replace />} />
                  </Routes>
                    </CelebrationProvider>
                  </CalendarSettingsProvider>
                </AppointmentsProvider>
              </NotificationProvider>
            </PinLockProvider>
          </LocationProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
